import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, resolveMedia } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { Trophy, Star, WarningCircle } from "@phosphor-icons/react";

const STATS = [
  { k: "pts", label: "PTS" },
  { k: "ast", label: "AST" },
  { k: "reb", label: "REB" },
  { k: "blk", label: "BLK" },
];

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" }) : "");

export function PlayerOfGameBanner() {
  const { settings } = useSettings();
  const [cards, setCards] = useState([]);
  const [division, setDivision] = useState("all");

  useEffect(() => {
    const q = division === "all" ? "" : `?division=${encodeURIComponent(division)}`;
    api.get(`/potg${q}`).then((r) => setCards(r.data || [])).catch(() => setCards([]));
  }, [division]);

  const divisions = useMemo(() => {
    const fromSettings = settings.divisions || [];
    const fromData = Array.from(new Set(cards.map((c) => c.division)));
    return Array.from(new Set([...fromSettings, ...fromData])).filter(Boolean);
  }, [settings.divisions, cards]);

  const recent = cards.slice(0, 3);

  return (
    <section className="relative border-y border-white/10 bg-[#0A0A0A]" data-testid="potg-banner">
      <div className="max-w-7xl mx-auto px-6 py-14">
        <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
          <div className="flex items-center gap-3">
            <Trophy size={26} weight="fill" className="text-[#FF5722]" />
            <div>
              <p className="text-[10px] tracking-[0.3em] uppercase text-[#FF5722] font-semibold">Best on the floor</p>
              <h2 className="font-display text-3xl sm:text-4xl uppercase leading-none">Player of the Game</h2>
            </div>
          </div>
          {cards.length > 0 && (
            <Link to="/player-of-the-week" data-testid="potg-history-link" className="text-xs uppercase font-bold tracking-wide text-white/60 hover:text-[#FF5722] transition-colors">View All →</Link>
          )}
        </div>

        {divisions.length > 1 && (
          <div className="flex gap-2 flex-wrap mb-8" data-testid="potg-banner-filters">
            <button onClick={() => setDivision("all")} data-testid="potg-banner-filter-all"
              className={`px-4 py-2 text-xs uppercase font-bold tracking-wide border transition-colors ${division === "all" ? "bg-[#FF5722] text-black border-[#FF5722]" : "border-white/15 text-white/60 hover:text-white"}`}>All Divisions</button>
            {divisions.map((d) => (
              <button key={d} onClick={() => setDivision(d)} data-testid={`potg-banner-filter-${d.replace(/\s+/g, "-").toLowerCase()}`}
                className={`px-4 py-2 text-xs uppercase font-bold tracking-wide border transition-colors ${division === d ? "bg-[#FF5722] text-black border-[#FF5722]" : "border-white/15 text-white/60 hover:text-white"}`}>{d}</button>
            ))}
          </div>
        )}

        {recent.length === 0 ? (
          <div className="border-2 border-dashed border-white/15 py-14 text-center" data-testid="potg-banner-placeholder">
            <Trophy size={38} className="text-white/30 mx-auto mb-3" />
            <p className="text-white/50">Player of the Game publishes automatically when a game is finalized. Check back soon!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {recent.map((c) => (
              <div key={c.game_id} data-testid={`potg-banner-card-${c.game_id}`} className="group relative border border-white/10 bg-[#121212] overflow-hidden hover:border-[#FF5722] transition-colors duration-200">
                <div className="relative aspect-[4/3] bg-[#0A0A0A] overflow-hidden">
                  {c.photo_url
                    ? <img src={resolveMedia(c.photo_url)} alt={c.player_name} className="w-full h-full object-cover" />
                    : <div className="w-full h-full flex items-center justify-center"><Star size={54} weight="fill" className="text-white/10" /></div>}
                  <span className="absolute top-3 left-3 bg-[#FF5722] text-black text-[10px] font-bold uppercase tracking-wide px-2.5 py-1 flex items-center gap-1"><Trophy size={12} weight="fill" /> Player of the Game</span>
                  <span className="absolute top-3 right-3 bg-black/70 backdrop-blur text-white text-[10px] font-semibold uppercase tracking-wide px-2.5 py-1">{c.division}</span>
                </div>
                <div className="p-5">
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-display text-2xl uppercase leading-tight group-hover:text-[#FF5722] transition-colors">{c.number != null ? `#${c.number} ` : ""}{c.player_name}</p>
                    {c.needs_review && <span title="Tie — flagged for review" className="text-[#FFB300]"><WarningCircle size={18} weight="fill" /></span>}
                  </div>
                  <p className="text-sm text-white/50 uppercase tracking-wide">{c.team_name}{c.opponent ? <span className="text-white/30"> vs {c.opponent}</span> : null}</p>
                  <p className="text-xs text-white/40 mt-1">{c.week != null ? `Week ${c.week} · ` : ""}{fmtDate(c.date)}{c.final_score ? ` · ${c.final_score}` : ""}</p>
                  <div className="grid grid-cols-4 gap-1 mt-4 border-t border-white/10 pt-4">
                    {STATS.map((s) => (
                      <div key={s.k} className="text-center">
                        <div className="font-display text-xl text-white tabular-nums leading-none">{c[s.k] || 0}</div>
                        <div className="text-[9px] uppercase tracking-wide text-white/40 mt-1">{s.label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
